import React from 'react';
import { useSelector } from 'react-redux';
import { useRequestData } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { galleryTrackingEvents } from '../../utilities/tracking';
import { GALLERY_TABS } from '../../constants/galleryTabs';
import { useGalleryContext } from '../../contexts/GalleryContext';
import GalleryButton from './GalleryButton';
import MyCarsSaveIcon from './MyCarsSaveIcon';

const GalleryToolbar = () => {
	const { actions, meta } = useGalleryContext();
	const { onOpenModal, setActiveTab } = actions;
	const { prefs, labels, galleryToolbarPlacement, isDesktop } = meta;
	const {
		galleryToolbarStyle,
		imgThumbStyle,
		showVehicleCount,
		showMyCarsSave
	} = prefs;
	const { imagesToDisplay = [], videos = [] } = useSelector(
		(state) => state.media
	);
	const { widgetName, windowId } = useRequestData();
	const isMobile = !isDesktop;

	const isOverlayMode = galleryToolbarStyle === 'none';

	// Handle undefined preference - default to 'false' if not set
	const isShowSaveBtn = showMyCarsSave === 'true';

	const isTextLinkMode =
		isOverlayMode || galleryToolbarStyle === 'text' || isMobile;

	const isSaveInToolbar = isShowSaveBtn && !isMobile && isOverlayMode;
	const isSaveInToolbarMobile =
		isShowSaveBtn &&
		isMobile &&
		isOverlayMode &&
		galleryToolbarPlacement === 'top' &&
		imgThumbStyle === 'horizontal';

	let btnSpace = '';
	if (!isOverlayMode) {
		btnSpace = isMobile || galleryToolbarStyle !== 'text' ? 'mx-3' : 'mx-5';
	} else {
		btnSpace = imgThumbStyle === 'horizontal' ? 'ml-3' : 'mr-3';
	}

	const hasVideo = videos.length > 0;

	if (imagesToDisplay.length === 0) {
		return null;
	}

	const thumbnailUrl = imagesToDisplay[0].thumbnail || '';

	const marginToolbar = galleryToolbarPlacement === 'top' ? 'mb-4' : 'mt-4';

	const positionToolbar =
		galleryToolbarPlacement === 'top'
			? 'absolute-top mx-4 mt-4'
			: 'absolute-bottom mx-4 mb-4';
	const vehicleLinkContainerClass = [
		'd-flex',
		'align-items-center',
		'justify-content-center',
		marginToolbar
	];

	const TOTAL_IMAGES = imagesToDisplay.length;

	const handleTrackingPhotoCountButton = () =>
		galleryTrackingEvents.clickPhotoCountToolbar(widgetName, windowId, {
			elementCTA: `${TOTAL_IMAGES} Photos`
		});
	const handleTrackingClickAllPhotos = () =>
		galleryTrackingEvents.clickAllPhotosButton(widgetName, windowId);
	const handleTrackingClickVideo = () =>
		galleryTrackingEvents.clickVideoButton(widgetName, windowId);

	let buttonVariant;
	if (isOverlayMode) {
		buttonVariant = 'overlay';
	} else if (isTextLinkMode) {
		buttonVariant = 'toolbar';
	} else {
		buttonVariant = 'toolbar-thumbnail';
	}

	const photoButtonLabel =
		showVehicleCount !== 'false'
			? `${TOTAL_IMAGES} ${labels.get('PHOTOS')}`
			: labels.get('ALL_PHOTOS');

	const handleTrackingPhotos =
		showVehicleCount !== 'false'
			? handleTrackingPhotoCountButton
			: handleTrackingClickAllPhotos;

	const photosButton = (
		<GalleryButton
			btnLabel={photoButtonLabel}
			btnSpace={btnSpace}
			handleClick={() => {
				onOpenModal();
				setActiveTab(GALLERY_TABS.PHOTOS);
				handleTrackingPhotos();
			}}
			icon="ddc-icon-images2"
			variant={buttonVariant}
			thumbnailUrl={thumbnailUrl}
		/>
	);

	const videoButton = hasVideo ? (
		<GalleryButton
			btnLabel={labels.get('VIDEO')}
			btnSpace={btnSpace}
			handleClick={() => {
				onOpenModal();
				setActiveTab(GALLERY_TABS.VIDEO);
				handleTrackingClickVideo();
			}}
			icon="ddc-icon-play3"
			variant={buttonVariant}
			thumbnailUrl={thumbnailUrl}
		/>
	) : null;

	if (isOverlayMode) {
		return (
			<span
				data-testid="overlay-toolbar"
				className={`vehicle-gallery-toolbar d-flex align-items-center ${positionToolbar} ${
					imgThumbStyle === 'horizontal' ? 'absolute-right' : ''
				}`}
			>
				{photosButton}
				{videoButton}
				{(isSaveInToolbar || isSaveInToolbarMobile) && (
					<MyCarsSaveIcon
						className={`gallery-mycars-save-icon ${
							isSaveInToolbarMobile ? 'mycars-mobile' : ''
						} ${btnSpace}`}
					/>
				)}
			</span>
		);
	}

	return (
		<div className={setClassNames(vehicleLinkContainerClass)}>
			{photosButton}
			{videoButton}
		</div>
	);
};

GalleryToolbar.propTypes = {};

export default GalleryToolbar;
